/**
 * What a document read turns up, for the demo.
 *
 * The document is the Midterm 1 paper in
 * `public/mock-docs/cs-101-nursing-midterm-1.html`, so what the reviewer sees
 * here is what that file says, question for question. Nursing content, because
 * the workspace is a school of nursing and a physical-therapy paper behind a
 * nursing course reads as someone else's data.
 *
 * Hand-written rather than generated, because the point of this fixture is to
 * put every review case on screen at once: a clean majority the reviewer can
 * wave through, a couple of soft flags, two items that cannot import until
 * someone fixes them, and two that already exist in the bank. A random
 * generator gives a different batch every reload and reliably fails to produce
 * the one case you wanted to look at.
 *
 * The confidence numbers are the interesting part. They are not decoration:
 * they are what lets the review route open on the four items worth reading
 * instead of on all sixteen, and what makes a single bulk accept safe. Read
 * them as "how sure was the extractor", not "how good is the question".
 */

import type { ExtractedQuestion, ImportedQuestionDraft } from "@/lib/question-import-model"

interface SeedItem {
  page: number
  confidence: number
  sourceText: string
  draft: Partial<ImportedQuestionDraft> & { stem: string; type: string }
  duplicate?: {
    questionId: string
    title: string
    stem: string
    score: number
    folderName: string
    timesUsed: number
    options?: string[]
    answerIndexes?: number[]
  }
}

const DEFAULT_DRAFT: ImportedQuestionDraft = {
  title: "",
  stem: "",
  type: "Multiple Choice",
  options: [],
  answerIndexes: [],
  rationale: "",
  difficulty: "",
  bloom: "",
  cogLevel: "",
  points: 1,
  topic: "",
  tags: [],
  folderId: "",
  randomizeOptions: true,
  multiSelectScoring: "exact",
  unlimitedSelections: false,
  partialCredit: false,
  caseSensitive: false,
  negativeMarking: false,
  showFeedbackWhenReviewing: true,
  eligibleForRandomDraw: true,
  shuffleMatchingPairs: true,
  shuffleOrderingItems: false,
}

/**
 * Sixteen questions off the Midterm 1 paper. The exercise item and the hand
 * hygiene blank lead the review. The rest stay in paper order.
 */
const SEED: SeedItem[] = [
  {
    page: 8,
    confidence: 0.82,
    sourceText:
      "16. Which vital sign change is expected in a healthy adult during moderate exercise?\n(a) Decrease in respiratory rate\n(b) Decrease in core body temperature\n(c) Marked elevation in diastolic blood pressure\n(d) Progressive elevation in systolic blood pressure",
    draft: {
      stem: "Which vital sign change is expected in a healthy adult during moderate exercise?",
      type: "Multiple Choice",
      options: [
        "Both systolic and diastolic fall",
        "Systolic falls, diastolic rises",
        "Systolic pressure rises, diastolic stays near baseline",
        "Neither changes",
      ],
      answerIndexes: [2],
      rationale:
        "Moderate exercise raises cardiac output, so systolic pressure rises while diastolic pressure stays near baseline.",
      difficulty: "Medium",
      bloom: "Understand",
      topic: "Vital Signs",
    },
  },
  {
    page: 2,
    confidence: 0.93,
    sourceText:
      "3. Hand hygiene with soap and water must last at least ____ seconds.",
    draft: {
      stem: "Hand hygiene with soap and water must last at least ____ seconds.",
      type: "Fill in the Blank",
      options: ["20", "twenty"],
      answerIndexes: [0, 1],
      rationale: "",
      difficulty: "",
      bloom: "",
      topic: "",
    },
  },
  {
    page: 1,
    confidence: 0.97,
    sourceText:
      "1. A client two hours post abdominal surgery has a blood pressure of 88/52, heart rate 118, and cool clammy skin. Which action does the nurse take FIRST?\n(a) Raise the legs and give oxygen while notifying the provider\n(b) Document the readings and recheck in one hour\n(c) Encourage oral fluids and ambulation\n(d) Give the scheduled opioid to settle the client",
    draft: {
      stem: "A client two hours post abdominal surgery has a blood pressure of 88/52, a heart rate of 118, and cool clammy skin. Which action does the nurse take first?",
      type: "Multiple Choice",
      options: [
        "Raise the legs and give oxygen while notifying the provider",
        "Document the readings and recheck in one hour",
        "Encourage oral fluids and ambulation",
        "Give the scheduled opioid to settle the client",
      ],
      answerIndexes: [0],
      rationale:
        "These are early signs of hypovolaemic shock. Restoring perfusion and escalating come before documentation or comfort measures.",
      difficulty: "Medium",
      bloom: "Apply",
      topic: "Med-Surg Nursing",
    },
  },
  {
    page: 1,
    confidence: 0.95,
    sourceText:
      "2. Which finding in a client receiving furosemide most needs the nurse to hold the dose and call the provider?\n(a) Urine output of 60 mL per hour\n(b) Serum potassium of 2.9 mEq/L\n(c) Weight loss of 0.5 kg since yesterday\n(d) Reported thirst",
    draft: {
      stem: "Which finding in a client receiving furosemide most needs the nurse to hold the dose and call the provider?",
      type: "Multiple Choice",
      options: [
        "Urine output of 60 mL per hour",
        "Serum potassium of 2.9 mEq/L",
        "Weight loss of 0.5 kg since yesterday",
        "Reported thirst",
      ],
      answerIndexes: [1],
      rationale:
        "Hypokalaemia below 3.0 mEq/L risks dysrhythmia, and a loop diuretic will drive potassium lower.",
      difficulty: "Medium",
      bloom: "Analyze",
      topic: "Pharmacology",
    },
    duplicate: {
      questionId: "CASE-2026-015",
      title: "Warfarin target",
      stem: "Which of the following statements about Warfarin is correct?",
      score: 0.94,
      folderName: "NUR-210 Pharmacology",
      timesUsed: 7,
      options: [
        "Urine output of 60 mL per hour",
        "Serum potassium of 2.9 mEq/L",
        "Weight loss of half a kilogram overnight",
        "Client-reported thirst",
      ],
      answerIndexes: [1],
    },
  },
  {
    page: 2,
    confidence: 0.68,
    sourceText:
      "4. Match each precaution to its illness.\nIllness | Precaution\nPulmonary tuberculosis |\nInfluenza |\nClostridioides difficile |\nAirborne · Droplet · Contact\nAnswer: Pulmonary tuberculosis = Airborne; Influenza = Droplet; Clostridioides difficile = Contact",
    draft: {
      stem: "Match each precaution to its illness.",
      type: "Matching",
      options: [],
      answerIndexes: [],
      pairs: [
        {
          id: "import-pair-0",
          left: "Pulmonary tuberculosis",
          right: "Airborne",
        },
        { id: "import-pair-1", left: "Influenza", right: "Droplet" },
        {
          id: "import-pair-2",
          left: "Clostridioides difficile",
          right: "Contact",
        },
      ],
      rationale: "",
      difficulty: "",
      bloom: "",
      topic: "Infection Control",
      points: 3,
    },
  },
  {
    page: 3,
    confidence: 0.96,
    sourceText:
      "5. Which entries belong in the objective section of a nursing note? Select all that apply.\n(a) Temperature of 38.4 degrees Celsius\n(b) Wound edges approximated with scant serous drainage\n(c) The client's report of throbbing pain\n(d) Bilateral crackles on auscultation",
    draft: {
      stem: "Which entries belong in the objective section of a nursing note? Select all that apply.",
      type: "Multiple Select",
      options: [
        "Temperature of 38.4 degrees Celsius",
        "Wound edges approximated with scant serous drainage",
        "The client's report of throbbing pain",
        "Bilateral crackles on auscultation",
      ],
      answerIndexes: [0, 1, 3],
      rationale:
        "A client's own report of pain is subjective. Everything measured or observed by the nurse is objective.",
      difficulty: "Easy",
      bloom: "Understand",
      topic: "Documentation",
    },
  },
  {
    page: 3,
    confidence: 0.91,
    sourceText:
      "6. True or false: a stage 2 pressure injury extends through the full thickness of the dermis into subcutaneous fat.",
    draft: {
      stem: "A stage 2 pressure injury extends through the full thickness of the dermis into subcutaneous fat.",
      type: "True / False",
      options: ["True", "False"],
      answerIndexes: [1],
      rationale:
        "Stage 2 is partial-thickness loss of the dermis. Full-thickness loss with visible fat is stage 3.",
      difficulty: "Easy",
      bloom: "Remember",
      topic: "Wound Care",
    },
  },
  {
    page: 4,
    confidence: 0.78,
    sourceText:
      "7. A client with COPD desaturates to 86% while walking to the bathroom. The MOST appropriate nursing response is:\n(a) Stop, seat the client, and reassess with pursed-lip breathing\n(b) \n(c) Increase the pace to build tolerance\n(d) Continue and document the reading",
    draft: {
      stem: "A client with COPD desaturates to 86% while walking to the bathroom. The most appropriate nursing response is:",
      type: "Multiple Choice",
      options: [
        "Stop, seat the client, and reassess with pursed-lip breathing",
        "Raise the oxygen flow and have the client keep walking",
        "Increase the pace to build tolerance",
        "Continue and document the reading",
      ],
      answerIndexes: [0],
      rationale: "",
      difficulty: "Medium",
      bloom: "Apply",
      topic: "Oxygenation",
    },
  },
  {
    page: 4,
    confidence: 0.94,
    sourceText:
      "8. A client is ordered 750 mg of an antibiotic. The vial reads 250 mg per 5 mL. Show your working and state the volume to administer.",
    draft: {
      stem: "A client is ordered 750 mg of an antibiotic and the vial reads 250 mg per 5 mL. Show your working and state the volume to administer.",
      type: "Essay",
      options: [],
      answerIndexes: [],
      rationale:
        "Look for the desired-over-have setup, 15 mL as the answer, and a stated safety check before administration.",
      difficulty: "Hard",
      bloom: "Create",
      topic: "Dosage Calculation",
      points: 5,
    },
  },
  {
    page: 5,
    confidence: 0.62,
    sourceText:
      "9. [figure] Name the site marked with an arrow for an intramuscular injection in an adult.",
    draft: {
      stem: "Name the site marked with an arrow for an intramuscular injection in an adult.",
      type: "Short Answer",
      options: [],
      answerIndexes: [],
      rationale: "Ventrogluteal site",
      difficulty: "",
      bloom: "",
      topic: "Medication Administration",
    },
  },
  {
    page: 5,
    confidence: 0.96,
    sourceText:
      "10. Which assessment best tells the nurse whether fluid replacement is working in a dehydrated client?\n(a) Hourly urine output\n(b) Skin turgor over the sternum\n(c) Reported thirst\n(d) Oral mucous membranes",
    draft: {
      stem: "Which assessment best tells the nurse whether fluid replacement is working in a dehydrated client?",
      type: "Multiple Choice",
      options: [
        "Hourly urine output",
        "Skin turgor over the sternum",
        "Reported thirst",
        "Oral mucous membranes",
      ],
      answerIndexes: [0],
      rationale:
        "Urine output is the most direct measure of renal perfusion, and it responds within the hour.",
      difficulty: "Easy",
      bloom: "Apply",
      topic: "Fluids and Electrolytes",
    },
  },
  {
    page: 6,
    confidence: 0.89,
    sourceText:
      "11. An older client is admitted after a fall at home. Which action does the nurse take first on arrival to the unit?\n(a) Complete a fall risk assessment\n(b) Order a walking frame\n(c) Teach home hazard removal\n(d) Refer to physiotherapy",
    draft: {
      stem: "An older client is admitted after a fall at home. Which action does the nurse take first on arrival to the unit?",
      type: "Multiple Choice",
      options: [
        "Complete a fall risk assessment",
        "Order a walking frame",
        "Teach home hazard removal",
        "Refer to physiotherapy",
      ],
      answerIndexes: [0],
      rationale:
        "Assessment precedes intervention, and the risk score drives which precautions the unit puts in place.",
      difficulty: "Medium",
      bloom: "Apply",
      topic: "Safety",
    },
    duplicate: {
      questionId: "CASE-2026-012",
      title: "Heart chamber for deoxygenated blood",
      stem: "Which chamber of the heart receives deoxygenated blood from the systemic veins?",
      score: 0.88,
      folderName: "NUR-210 Fundamentals",
      timesUsed: 3,
    },
  },
  {
    page: 6,
    confidence: 0.95,
    sourceText:
      "12. Normal resting respiratory rate for a healthy adult is between ____ and ____ breaths per minute.",
    draft: {
      stem: "Normal resting respiratory rate for a healthy adult is between ____ and ____ breaths per minute.",
      type: "Fill in the Blank",
      options: ["12", "20"],
      answerIndexes: [0, 1],
      rationale: "Twelve to twenty breaths per minute is the accepted range.",
      difficulty: "Easy",
      bloom: "Remember",
      topic: "Vital Signs",
    },
  },
  {
    page: 7,
    confidence: 0.92,
    sourceText:
      "13. Which findings after a blood transfusion begins require the nurse to stop the infusion? Select all that apply.\n(a) Temperature rise of 1.2 degrees Celsius\n(b) Flank pain with dark urine\n(c) Mild anxiety about the needle\n(d) New wheeze and facial flushing",
    draft: {
      stem: "Which findings after a blood transfusion begins require the nurse to stop the infusion? Select all that apply.",
      type: "Multiple Select",
      options: [
        "Temperature rise of 1.2 degrees Celsius",
        "Flank pain with dark urine",
        "Mild anxiety about the needle",
        "New wheeze and facial flushing",
      ],
      answerIndexes: [0, 1, 3],
      rationale:
        "Anxiety about the needle is not a transfusion reaction. The other three point at febrile, haemolytic, and allergic reactions.",
      difficulty: "Medium",
      bloom: "Analyze",
      topic: "Med-Surg Nursing",
      points: 2,
    },
  },
  {
    page: 7,
    confidence: 0.97,
    sourceText:
      "14. Before giving digoxin, the nurse checks which parameter?\n(a) Apical heart rate for one full minute\n(b) Respiratory rate\n(c) Oral temperature\n(d) Pupil reaction",
    draft: {
      stem: "Before giving digoxin, the nurse checks which parameter?",
      type: "Multiple Choice",
      options: [
        "Apical heart rate for one full minute",
        "Respiratory rate",
        "Oral temperature",
        "Pupil reaction",
      ],
      answerIndexes: [0],
      rationale:
        "Digoxin slows conduction, so the dose is held and reported when the apical rate is under 60 beats per minute.",
      difficulty: "Easy",
      bloom: "Remember",
      topic: "Pharmacology",
    },
  },
  {
    page: 8,
    confidence: 0.86,
    sourceText:
      "15. A client refuses a prescribed medication. Give two nursing actions and the rationale for each.",
    draft: {
      stem: "A client refuses a prescribed medication. Give two nursing actions and the rationale for each.",
      type: "Short Answer",
      options: [],
      answerIndexes: [],
      rationale: "",
      difficulty: "Hard",
      bloom: "Evaluate",
      topic: "Ethics and Communication",
      points: 3,
    },
  },
]

/** How many questions a read of this fixture turns up. */
export const MOCK_EXTRACTION_COUNT = SEED.length

/**
 * The midterm file we hand out is this fixture, question for question.
 * Parsing that Word file again produces a different read every time the
 * rules shift, so an upload of it always uses the seeded questions and answers.
 */
export function usesSeededMidtermRead(name: string): boolean {
  return (
    /nurs-210/i.test(name) ||
    /nursing midterm/i.test(name) ||
    /midterm 1 question paper/i.test(name)
  )
}

/**
 * Build the batch for one job.
 *
 * `count` trims the fixture so the demo can show a short read without a second
 * set of questions to maintain.
 */
export function mockExtractedQuestions(
  jobId: string,
  count = SEED.length
): ExtractedQuestion[] {
  return SEED.slice(0, Math.max(0, count)).map((seed, index) => ({
    id: `${jobId}-item-${index + 1}`,
    ordinal: index + 1,
    confidence: seed.confidence,
    page: seed.page,
    sourceText: seed.sourceText,
    duplicate: seed.duplicate ?? null,
    duplicateResolution: "unresolved" as const,
    decision: "pending" as const,
    importedAt: null,
    draft: { ...DEFAULT_DRAFT, ...seed.draft },
  }))
}
